import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import pocketbaseService from '../../services/pocketbase';

const {
  FiPlus,
  FiInbox,
  FiGrid,
  FiX,
  FiArrowRight,
  FiCheckCircle,
  FiHelpCircle,
  FiCopy,
} = FiIcons;

const normalizeAppId = (value) => value
  .toLowerCase()
  .replace(/[\s-]+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_+/, '');

const validAppId = (value) => /^[a-z][a-z0-9_]{2,48}[a-z0-9]$/.test(value);

const QuickCreateMenu = ({ open, onOpen, onClose, onCreateTicket, onOpenHelp }) => {
  const { isAdmin } = useAuth();
  const { refreshApps, showToast } = useApp();
  const [mode, setMode] = useState('menu');
  const [saving, setSaving] = useState(false);
  const [idTouched, setIdTouched] = useState(false);
  const [error, setError] = useState('');
  const [createdApp, setCreatedApp] = useState(null);
  const [form, setForm] = useState({ appName: '', appId: '', isActive: true });

  const appIdValid = useMemo(() => validAppId(form.appId.replace(/_+$/, '')), [form.appId]);

  const reset = () => {
    setMode('menu');
    setSaving(false);
    setIdTouched(false);
    setError('');
    setCreatedApp(null);
    setForm({ appName: '', appId: '', isActive: true });
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleName = (value) => {
    setForm((current) => ({
      ...current,
      appName: value,
      appId: idTouched ? current.appId : normalizeAppId(value),
    }));
    setError('');
  };

  const createApp = async (event) => {
    event.preventDefault();
    const appId = form.appId.replace(/_+$/, '');
    if (!form.appName.trim()) {
      setError('Enter an App Name.');
      return;
    }
    if (!validAppId(appId)) {
      setError('App ID must use lowercase letters, numbers and underscores, start with a letter, and not end with an underscore.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const record = await pocketbaseService.createApp({ appName: form.appName.trim(), appId, isActive: form.isActive });
      await refreshApps();
      setCreatedApp(record);
      setMode('success');
      showToast('App created');
    } catch (createError) {
      setError(createError?.message || 'Unable to create app.');
    } finally {
      setSaving(false);
    }
  };

  const copyAppId = async () => {
    if (!createdApp?.appId) return;
    try {
      await navigator.clipboard.writeText(createdApp.appId);
      showToast('App ID copied');
    } catch {
      showToast('Could not copy App ID', 'error');
    }
  };

  return (
    <>
      <button type="button" onClick={onOpen} className="quick-create-fab" aria-label="Create ticket or app" title="Create">
        <SafeIcon icon={FiPlus} className="w-6 h-6" />
      </button>

      {open && (
        <div className="quick-create-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
          <section className="quick-create-panel" role="dialog" aria-modal="true" aria-label="Create">
            <div className="quick-create-handle" />
            <header className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-2 pb-4 border-b border-border">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Quick create</p>
                <h2 className="text-xl font-bold text-text mt-1">
                  {mode === 'app' ? 'Create App' : mode === 'success' ? 'App created' : 'What would you like to create?'}
                </h2>
                <p className="text-sm text-muted mt-1">
                  {mode === 'app' ? 'Register a client app in Support Hub.' : mode === 'success' ? 'The app is ready for the integration prompt.' : 'Choose an action and Support Hub will guide you through it.'}
                </p>
              </div>
              <button type="button" onClick={close} disabled={saving} className="quick-create-close" aria-label="Close"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
            </header>

            {mode === 'menu' && (
              <div className="p-5 sm:p-6 grid gap-3">
                <button type="button" onClick={() => { close(); onCreateTicket(); }} className="quick-create-option">
                  <span className="quick-create-option-icon"><SafeIcon icon={FiInbox} className="w-6 h-6" /></span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block font-bold text-text">Create Ticket</span>
                    <span className="block text-sm text-muted mt-0.5">Log a support request manually for a client, call or internal issue.</span>
                  </span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-muted" />
                </button>

                {isAdmin && (
                  <button type="button" onClick={() => setMode('app')} className="quick-create-option">
                    <span className="quick-create-option-icon"><SafeIcon icon={FiGrid} className="w-6 h-6" /></span>
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block font-bold text-text">Create App</span>
                      <span className="block text-sm text-muted mt-0.5">Register a client app, then generate its GitHub Copilot integration prompt.</span>
                    </span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-muted" />
                  </button>
                )}
              </div>
            )}

            {mode === 'app' && (
              <form onSubmit={createApp} className="p-5 sm:p-6 space-y-5">
                <label className="block">
                  <span className="block text-sm font-semibold text-text mb-1.5">App Name</span>
                  <input autoFocus required value={form.appName} onChange={(event) => handleName(event.target.value)} className="control" placeholder="Storage Service Business Hub" />
                  <span className="block text-xs text-muted mt-1.5">Friendly name shown throughout Support Hub.</span>
                </label>

                <label className="block">
                  <span className="block text-sm font-semibold text-text mb-1.5">App ID</span>
                  <input required value={form.appId} onChange={(event) => { setIdTouched(true); setForm((current) => ({ ...current, appId: normalizeAppId(event.target.value) })); setError(''); }} className="control font-mono" placeholder="storage_service_business_hub_app" />
                  <span className={`block text-xs mt-1.5 ${form.appId && appIdValid ? 'text-accent' : 'text-muted'}`}>
                    {form.appId && appIdValid ? 'Valid App ID. Keep this stable after integration.' : 'Lowercase letters, numbers and underscores only.'}
                  </span>
                </label>

                <label className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background px-4 py-3">
                  <span><span className="block text-sm font-semibold text-text">Active</span><span className="block text-xs text-muted mt-0.5">Active apps can receive support tickets.</span></span>
                  <input type="checkbox" checked={form.isActive} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))} className="w-5 h-5" />
                </label>

                {error && <div className="rounded-xl border border-border bg-primary-soft text-text px-4 py-3 text-sm"><span className="font-semibold text-primary">Check this:</span> {error}</div>}

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setMode('menu')} disabled={saving} className="px-4 py-2.5 rounded-xl border border-border bg-surface hover:bg-background font-semibold">Back</button>
                  <button type="submit" disabled={saving || !appIdValid || !form.appName.trim()} className="px-5 py-2.5 rounded-xl bg-primary text-surface hover:bg-primary-hover font-semibold disabled:opacity-40">{saving ? 'Creating...' : 'Create App'}</button>
                </div>
              </form>
            )}

            {mode === 'success' && createdApp && (
              <div className="p-5 sm:p-6">
                <div className="flex items-start gap-4 rounded-2xl border border-border bg-accent-soft p-4">
                  <span className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center shrink-0"><SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-text" /></span>
                  <div className="min-w-0">
                    <p className="font-bold text-text">{createdApp.appName} is registered.</p>
                    <button type="button" onClick={copyAppId} className="mt-1 inline-flex items-center gap-1.5 text-xs font-mono text-primary hover:underline"><SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />{createdApp.appId}</button>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-border bg-surface p-5">
                  <div className="flex items-center gap-2 text-primary font-semibold"><SafeIcon icon={FiHelpCircle} className="w-5 h-5" />Next step</div>
                  <ol className="mt-4 space-y-3 text-sm text-text">
                    <li><strong>1.</strong> Open Help Center.</li>
                    <li><strong>2.</strong> Select <strong>{createdApp.appName}</strong> from the registered app dropdown.</li>
                    <li><strong>3.</strong> Review the integration variables. Normally App Name and App ID are all you need to change.</li>
                    <li><strong>4.</strong> Copy or download the generated GitHub Copilot prompt and run it inside the target client app repository.</li>
                    <li><strong>5.</strong> Submit one test ticket and confirm it appears in this Support Hub.</li>
                  </ol>
                </div>

                <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <button type="button" onClick={close} className="px-4 py-2.5 rounded-xl border border-border bg-surface hover:bg-background font-semibold">Done</button>
                  <button type="button" onClick={() => { close(); onOpenHelp(); }} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-surface hover:bg-primary-hover font-semibold"><SafeIcon icon={FiHelpCircle} className="w-4 h-4" />Go to Help Center</button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default QuickCreateMenu;